import React from 'react';
import styled from 'styled-components';

const StarContainer = styled.div<{ size?: 'small' | 'medium' | 'large' }>`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.size === 'small' ? '2px' : props.size === 'large' ? '6px' : '4px'};
`;

const Star = styled.span<{ 
  $filled: boolean; 
  $interactive?: boolean; 
  size?: 'small' | 'medium' | 'large';
}>`
  font-size: ${props => 
    props.size === 'small' ? '14px' : 
    props.size === 'large' ? '24px' : '18px'
  };
  color: ${props => props.$filled ? '#FFD700' : '#4a4a4a'};
  cursor: ${props => props.$interactive ? 'pointer' : 'default'};
  transition: color 0.2s ease, transform 0.1s ease;
  user-select: none;

  ${props => props.$interactive && `
    &:hover {
      color: #FFD700;
      transform: scale(1.1);
    }
  `}
`;

const RatingText = styled.span<{ size?: 'small' | 'medium' | 'large' }>`
  color: #00ff88;
  font-family: 'Courier New', monospace;
  font-size: ${props => 
    props.size === 'small' ? '12px' : 
    props.size === 'large' ? '18px' : '14px'
  };
  font-weight: bold;
  margin-left: ${props => props.size === 'small' ? '6px' : '8px'};
`;

interface StarRatingProps {
  rating: number; // 0-5
  maxRating?: number; // default 5
  size?: 'small' | 'medium' | 'large'; // default medium
  readOnly?: boolean; // default true
  showRating?: boolean; // показывать число рядом со звездами
  onRatingChange?: (rating: number) => void;
  className?: string;
}

const StarRating: React.FC<StarRatingProps> = ({
  rating,
  maxRating = 5,
  size = 'medium',
  readOnly = true,
  showRating = false,
  onRatingChange,
  className
}) => {
  const [hoverRating, setHoverRating] = React.useState<number>(0);

  const handleStarClick = (starRating: number) => {
    if (!readOnly && onRatingChange) {
      onRatingChange(starRating);
    }
  };

  const handleStarEnter = (starRating: number) => {
    if (!readOnly) {
      setHoverRating(starRating);
    }
  };

  const handleStarLeave = () => {
    if (!readOnly) {
      setHoverRating(0);
    }
  };

  const displayRating = hoverRating || rating;
  const roundedRating = Math.round(displayRating);

  return (
    <StarContainer size={size} className={className}>
      {Array.from({ length: maxRating }, (_, index) => {
        const starNumber = index + 1;
        const isFilled = starNumber <= roundedRating;
        
        return (
          <Star
            key={starNumber}
            $filled={isFilled}
            $interactive={!readOnly}
            size={size}
            onClick={() => handleStarClick(starNumber)}
            onMouseEnter={() => handleStarEnter(starNumber)}
            onMouseLeave={handleStarLeave}
            role={!readOnly ? 'button' : undefined}
            tabIndex={!readOnly ? 0 : undefined}
            onKeyDown={(e) => {
              if (!readOnly && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                handleStarClick(starNumber);
              }
            }}
          >
            ★
          </Star>
        );
      })}
      
      {showRating && (
        <RatingText size={size}>
          {rating.toFixed(1)}
        </RatingText>
      )}
    </StarContainer>
  );
};

export default StarRating;